import {
  SetStateAction,
  Dispatch,
  useState,
  useCallback,
  useEffect,
  memo,
} from 'react'
import type { SortingRule } from 'react-table'
import { mdiArrowDown, mdiArrowUp } from '@mdi/js'

import theme from 'theme_'
import SortPopup from './SortPopup'
import type { TableHeader } from '../types'
import { getSortLabel } from '../utils'
import { MobileSortWrapper, SortContainer, SortIcon, SortLabel } from './styles'

interface MobileSortProps<T> {
  headers: Record<string, TableHeader>
  sortOptions: Array<SortingRule<T>>
  sortBy: SortingRule<T>
  setSortBy: Dispatch<SetStateAction<SortingRule<T>>>
}

function MobileSort<T>({
  headers,
  sortOptions,
  sortBy,
  setSortBy,
}: MobileSortProps<T>) {
  const isTabletQuery = window.matchMedia(theme.devices.tablet)
  const [isOpen, setIsOpen] = useState(false)
  const toggleIsOpen = () => setIsOpen(!isOpen)

  const resizeEventListener = useCallback(
    (e: MediaQueryListEvent) => {
      if (e.matches && isOpen) {
        setIsOpen(false)
      }
    },
    [isOpen]
  )

  useEffect(() => {
    isTabletQuery.addEventListener('change', resizeEventListener)
    return () => {
      isTabletQuery.removeEventListener('change', resizeEventListener)
    }
  }, [isTabletQuery, resizeEventListener])

  return (
    <MobileSortWrapper>
      <SortContainer onClick={toggleIsOpen}>
        <SortIcon path={sortBy.desc ? mdiArrowDown : mdiArrowUp} />
        <SortLabel>{getSortLabel(headers, sortBy)}</SortLabel>
      </SortContainer>
      {isOpen && (
        <SortPopup<T>
          headers={headers}
          handleClose={() => setIsOpen(false)}
          sortOptions={sortOptions}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
      )}
    </MobileSortWrapper>
  )
}

// 'as typeof MobileSort' give us ability to pass external types to memo component
export const MobileSortBy = memo(MobileSort) as typeof MobileSort
